import React, { useEffect, useState, Suspense } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import productAPI from "../../api/productApi";
import SEO from "../../components/SEO";
import ProductCard from "./Card";
import { Col, Row } from "react-bootstrap";
import {
  Heart,
  Ticket,
} from "lucide-react";

import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import Breadcrump from "../../components/Breadcrump";
import FloatingVideoWidget from "../../components/FloatingVideoWidget";
import { extractProductVideo } from "../../utils/videoHelper";
import cartAPI from "../../api/cartApi";
import SizeChart from "./SizeChart";

function DetailPage() {

  const { slug } = useParams();
  const {
   addToCart,
   setShowCartCanvas,
   cartSummary 
 } = useCart();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [detail, setDetail] = useState(null);
  const [variants, setVariants] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [displayedImages, setDisplayedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [sizeChartOpen, setSizeChartOpen] = useState(false);
  const [mainImage, setMainImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const [title, setTitle] = useState("");
  const [brand, setBrand] = useState({});
  const [skuCode, setSkuCode] = useState("");
  const [category, setCategory] = useState({});
  const [description, setDescription] = useState("");
  const [couponList, setCouponList] = useState([]);
  const [couponCode, setCouponCode] = useState(cartSummary?.couponCode || "");
  const [activeCoupon, setActiveCoupon] = useState(cartSummary?.couponCode || "");

  const fetchProductDetail = async () => {

    try {

      setLoading(true);

      const res = await productAPI.details(slug);

      if (res.code === 200) {

        const {
          product,
          detail,
          brand,
          category,
          variants,
          relatedProducts,
          isWishlisted,
        } = res.data;

        setProduct(product);
        setDetail(detail);
        setBrand(brand);
        setCategory(category);
        setVariants(variants || []);
        setRelatedProducts(relatedProducts || []);
        setIsWishlisted(isWishlisted || false);

        setTitle(product?.title);
        setSkuCode(product?.skuCode);
        setDescription(detail?.description);

        if (variants?.length > 0) {
          setSelectedVariant(variants[0]);
        }

        const images = [];

        if (product?.thumbnail) {
          images.push(product.thumbnail);
        }

        if (detail?.images?.length) {
          images.push(...detail.images);
        }

        if (variants?.length) {

          variants.forEach((v) => {

            if (v.images?.length) {
              images.push(...v.images);
            }

          });

        }

        setDisplayedImages(images);

        if (images.length > 0) {
          setMainImage(images[0]);
        }

      }

    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCouponCodes = async () => {
    try {
      const res = await cartAPI.showCoupon();
      if (res.code === 200) {
        setCouponList(res.data);
      }
    } catch (err) {
      console.error("Error fetching coupons:", err);
    }
  };

  useEffect(() => {
    fetchProductDetail();
  }, [slug]);

  useEffect(() => {
    fetchCouponCodes();
  }, []);

  useEffect(() => {
    setCouponCode(cartSummary?.couponCode || "");
    setActiveCoupon(cartSummary?.couponCode || "");
  }, [cartSummary]);

  const attributeMap = {};

  variants.forEach((variant) => {

    Object.entries(variant.attributes || {}).forEach(([key, value]) => {

      if (!attributeMap[key]) {
        attributeMap[key] = new Set();
      }

      attributeMap[key].add(value);

    });

  });

  // Size chart sirf un products pe dikhana hai jinke variants mein
  // ek "Size" attribute ho (dresses, tops, shirts, etc). Handbags,
  // home-decor jaise categories mein "Size" attribute hota hi nahi,
  // to wo apne aap hide ho jayega — koi category list maintain nahi karni.
  const hasSizeAttribute = Object.keys(attributeMap).some(
    (key) => key.trim().toLowerCase() === "size"
  );

  const handleVariantClick = (key, value) => {

    const updated = {
      ...selectedAttributes,
      [key]: value,
    };

    setSelectedAttributes(updated);

    const matched = variants.find((v) =>
      Object.entries(updated).every(
        ([k, val]) => v.attributes?.[k] === val
      )
    );

    if (matched) {

      setSelectedVariant(matched);

      if (matched.images?.length) {

        setDisplayedImages(matched.images);
        setMainImage(matched.images[0]);

      }
    }
  };

  useEffect(() => {

    const defaults = {};

    Object.entries(attributeMap).forEach(([key, values]) => {
      defaults[key] = [...values][0];
    });

    setSelectedAttributes(defaults);

  }, [variants]);

  const handleAddToCart = async(e) => {

    const action = e.currentTarget.dataset.action;

    addToCart(product._id, quantity, selectedVariant?._id);

     setShowCartCanvas(true);

    if (action === "buy-now") {
      navigate("/checkout");
    }
  };

  const handleWishlistToggle = async () => {

    const logged = Auth.getToken();

    if (!logged) {
      return showError("Please login first");
    }

    try {

      const res = await wishlistAPI.addProduct({
        productId: product?._id,
      });

      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }

    } catch (error) {
      showError("Something went wrong");
    }
  };

  const handleCopyCoupon = (code) => {
    if (!code) return;
    navigator.clipboard?.writeText(code);
    showSuccess?.(`Coupon "${code}" copied!`);
  };

  if (loading || !product) {
    return <div className="text-center py-5">Loading...</div>;
  }

  const price = selectedVariant
    ? selectedVariant.price
    : product?.price;

  const mrp = selectedVariant
    ? selectedVariant.mrp
    : product?.mrp;

  const inStock = selectedVariant
    ? Number(selectedVariant.stock) > 0
    : true;

  const productVideo = extractProductVideo({ ...product, detail });

  return (
    <>
      <Breadcrump title={title} />

      <SEO meta={detail?.meta} />

      <section className="luxury-product-detail-area py-5">

        <div className="container">

          <div className="row g-5 align-items-start">

            {/* LEFT GALLERY */}
            <div className="col-md-7">

              <div className="luxury-gallery-sticky-holder">

                <Row className="g-3 luxury-strict-editorial-grid">

                  {displayedImages.map((img, index) => (

                    <Col
                      xs={6}
                      key={index}
                      className="mb-2 d-flex align-items-stretch"
                    >

                      <div
                        className={`luxury-grid-image-card w-100 ${
                          mainImage === img ? "grid-card-active" : ""
                        }`}
                        onClick={() => setMainImage(img)}
                      >

                        <img
                          src={img}
                          alt=""
                          className="luxury-grid-asset-img"
                        />

                        <span className="grid-image-index-badge">
                          0{index + 1}
                        </span>

                      </div>

                    </Col>
                  ))}

                </Row>

              </div>

            </div>

            {/* RIGHT CONTENT */}
            <div className="col-md-5">

              {brand?.name && (
                <div className="luxury-brand-anchor-label mb-2">
                  <Link to={`/brands/${brand?.slug}`}>
                    {brand?.name}
                  </Link>
                </div>
              )}

              <h1 className="luxury-product-main-headline mb-3">
                {title}
              </h1>

              <div className="luxury-price-segment-block d-flex align-items-center gap-3 mb-4">

                <span className="luxury-final-price-tag">
                  ₹{Number(price).toFixed(0)}
                </span>

                {mrp > price && (
                  <>
                    <span className="luxury-slashed-mrp-tag">
                      ₹{Number(mrp).toFixed(0)}
                    </span>

                    <span className="luxury-discount-percentage-badge">
                      ({Math.round(((mrp - price) / mrp) * 100)}% OFF)
                    </span>
                  </>
                )}

              </div>

              <div className="luxury-sku-category-specs mb-4">

                {skuCode && (
                  <div className="spec-line-item">
                    SKU: <strong>{skuCode}</strong>
                  </div>
                )}

                {category?.name && (
                  <div className="spec-line-item">
                    Category:{" "}
                    <Link to={`/categories/${category?.slug}`}>
                      {category?.name}
                    </Link>
                  </div>
                )}

              </div>

              {/* 🌟 STOCK STATUS + BLINKING COUPON STICKERS ROW */}
              <div className="luxury-stock-coupon-row mb-4">

                {inStock ? (
                  <span className="stock-pill in-stock-color">
                    In Stock
                  </span>
                ) : (
                  <span className="stock-pill out-stock-color">
                    Out Of Stock
                  </span>
                )}

                {couponList?.length > 0 && (
                  <div className="luxury-coupon-sticker-track">
                    {couponList.map((coupon, idx) => (
                      <button
                        type="button"
                        key={coupon?._id || idx}
                        className="luxury-coupon-sticker"
                        
                        title={coupon?.description || "Tap to copy code"}
                      >
                        <Ticket size={12} />
                        {coupon?.code}
                      </button>
                    ))}
                  </div>
                )}

              </div>

              {detail?.shortDescription && (
                <p className="luxury-product-short-abstract mb-4">
                  {detail?.shortDescription}
                </p>
              )}

              <hr className="my-4" />

              {/* ATTRIBUTES + WISHLIST TEXT BUTTON BESIDE */}
              <div className="luxury-attributes-swatch-picker mb-4">

                <div className="luxury-attribute-header-row mb-3">
                  <span className="attribute-picker-label">Select Options</span>

                  <button
                    type="button"
                    className={`luxury-wishlist-inline-btn ${
                      isWishlisted ? "wishlisted-active" : ""
                    }`}
                    onClick={handleWishlistToggle}
                  >
                    <Heart
                      size={14}
                      strokeWidth={isWishlisted ? 0 : 1.8}
                      fill={isWishlisted ? "currentColor" : "none"}
                    />
                    {isWishlisted ? "Wishlisted" : "Add To Wishlist"}
                  </button>
                </div>

                {Object.entries(attributeMap).map(([attrKey, values]) => (

                  <div key={attrKey} className="mb-4">

                    <div className="attribute-picker-label mb-2">
                      {attrKey}
                    </div>

                    <div className="d-flex flex-wrap gap-2">

                      {[...values].map((value, idx) => {

                        const active =
                          selectedAttributes[attrKey] === value;

                        return (
                          <label
                            key={idx}
                            className={`luxury-attribute-pill-chip ${
                              active ? "chip-active" : ""
                            }`}
                          >

                            <input
                              type="radio"
                              hidden
                              checked={active}
                              onChange={() =>
                                handleVariantClick(attrKey, value)
                              }
                            />

                            {value}

                          </label>
                        );
                      })}

                    </div>

                  </div>
                ))}

                {/* SIZE CHART — accordion, variants (Size) ke niche aur quantity/add to cart ke upar.
                    Sirf tab dikhega jab is product ke variants mein "Size" attribute ho. */}
                {hasSizeAttribute && (
                  <div className="luxury-spec-item luxury-size-chart-accordion">

                    <button
                      type="button"
                      className={`luxury-spec-btn ${
                        sizeChartOpen ? "active-spec-btn" : ""
                      }`}
                      onClick={() => setSizeChartOpen((prev) => !prev)}
                    >

                      <span className="spec-main-title">
                        Size Chart
                      </span>

                      <span className="spec-icon">
                        {sizeChartOpen ? "−" : "+"}
                      </span>

                    </button>

                    <div
                      className={`luxury-spec-content ${
                        sizeChartOpen ? "open-spec-content" : ""
                      }`}
                    >

                      {sizeChartOpen && <SizeChart />}

                    </div>

                  </div>
                )}

              </div>

              {/* QUANTITY + ADD TO CART (same row), BUY NOW (below full width) */}
              <div className="luxury-interactive-actions-panel">

                <div className="d-flex gap-3 align-items-stretch mb-3 luxury-qty-cart-row">

                  <div className="luxury-quantity-stepper-box">

                    <button
                      className="stepper-btn"
                      onClick={() =>
                        quantity > 1 &&
                        setQuantity(quantity - 1)
                      }
                    >
                      -
                    </button>

                    <span className="stepper-current-val">
                      {quantity}
                    </span>

                    <button
                      className="stepper-btn"
                      onClick={() =>
                        setQuantity(quantity + 1)
                      }
                    >
                      +
                    </button>

                  </div>

                  <button
                    className="luxury-add-to-bag-btn-solid flex-grow-1 py-3"
                    onClick={handleAddToCart}
                  >
                    ADD TO CART
                  </button>

                </div>

                {/* 🌟 BUY NOW — full width niche */}
                <button
                  className="luxury-buy-now-btn-outline w-100 mb-4"
                  onClick={handleAddToCart}
                  data-action="buy-now"
                >
                  BUY NOW
                </button>

                {/* PRODUCT HIGHLIGHTS */}
                {detail?.keyFeatures?.length > 0 && (

                  <div className="inline-product-highlights mt-4 pt-4 border-top">

                    <h6 className="product-highlight-main-heading mb-4">
                      Product Highlights
                    </h6>

                    <div className="luxury-product-specs-accordion">

                      {detail.keyFeatures.map((feature, idx) => {

                        const isOpen = openAccordion === idx;

                        return (

                          <div
                            className="luxury-spec-item"
                            key={idx}
                          >

                            <button
                              type="button"
                              className={`luxury-spec-btn ${
                                isOpen ? "active-spec-btn" : ""
                              }`}
                              onClick={() =>
                                setOpenAccordion(isOpen ? null : idx)
                              }
                            >

                              <span className="spec-main-title">
                                {feature?.label}
                              </span>

                              <span className="spec-icon">
                                {isOpen ? "−" : "+"}
                              </span>

                            </button>

                            <div
                              className={`luxury-spec-content ${
                                isOpen ? "open-spec-content" : ""
                              }`}
                            >

                              <div className="luxury-spec-description">
                                {feature?.value}
                              </div>

                            </div>

                          </div>
                        );
                      })}

                    </div>

                  </div>
                )}

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* DESCRIPTION */}
      {description && (
        <section className="py-5 bg-white">

          <div className="container">

            <h3 className="section-segment-heading mb-4">
              Description
            </h3>

            <div
              className="editorial-html-injected-content-container"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />

          </div>

        </section>
      )}

      {/* RELATED PRODUCTS */}
      {relatedProducts?.length > 0 && (
        <section className="py-5 bg-light">

          <div className="container">

            <h3 className="section-segment-heading mb-4">
              You May Also Like
            </h3>

            <div className="row g-4">

              <Suspense fallback={<div>Loading...</div>}>

                {relatedProducts.slice(0, 4).map((item, idx) => (

                  <div
                    className="col-lg-3 col-6"
                    key={item?._id || idx}
                  >
                    <ProductCard item={item} />
                  </div>

                ))}

              </Suspense>

            </div>

          </div>

        </section>
      )}

      {/* STICKY BOTTOM PURCHASE BAR */}
      <div className="luxury-sticky-purchase-bar">

        <div className="sticky-product-mini-info">

          <div className="sticky-product-thumb">

            <img
              src={mainImage}
              alt={title}
            />

          </div>

          <div className="sticky-product-content">

            <h6>{title}</h6>

            <div className="sticky-price-wrap">

              <span className="sticky-final-price">
                ₹{Number(price).toFixed(0)}
              </span>

              {mrp > price && (
                <span className="sticky-old-price">
                  ₹{Number(mrp).toFixed(0)}
                </span>
              )}

            </div>

          </div>

        </div>

        <div className="sticky-action-buttons">

          <button
            className="sticky-cart-btn "
            onClick={handleAddToCart}
          >
            Add To Cart
          </button>

          <button
            className="sticky-buy-btn"
            onClick={handleAddToCart}
            data-action="buy-now"
          >
            Buy Now
          </button>

        </div>

      </div>

      <FloatingVideoWidget src={productVideo} title={title} />
    </>
  );
}

export default DetailPage;